/** A point in normalized image space: (0,0) top-left, (1,1) bottom-right. */
export interface Vec2 {
  x: number;
  y: number;
}

export interface Photo {
  uri: string;
  width: number;
  height: number;
}

export type LightLayout = 'single' | 'pair';

/** How the LED nodes are laid out and how they look. Physical units are inches/feet. */
export interface LightStyle {
  layout: LightLayout;
  /** Distance between nodes (or between pair centers), inches. */
  spacingIn: number;
  /** Distance between the two LEDs inside a pair, inches. */
  pairGapIn: number;
  /** Visual LED size multiplier (1 = typical 1" faceted lens). */
  size: number;
  /** Light output multiplier (1 = typical). */
  brightness: number;
  /** How far the light wash reaches down the wall, feet. */
  washFt: number;
  /** Multi-color themes with pairs: both LEDs of a pair share a color, or colors alternate LED by LED. */
  pairColors?: 'together' | 'alternate';
}

/** A continuous run of lights the installer drew along the house. */
export interface Run {
  id: string;
  points: Vec2[];
  /** Line color (one of LINE_COLORS): groups runs for the legend and pricing. */
  lineColor: string;
}

/** What a line color stands for in the legend: a product and its price. */
export interface LegendLabel {
  name: string;
  /** Product / bulb description, e.g. "RGBW pairs, 12 in". */
  product: string;
  /** Price per linear foot, in the user's currency. */
  pricePerFt: number | null;
}

/** 0 = midday, 0.5 = evening / blue hour, 1 = full night. */
export type TimeOfDay = number;

export type TimePreset = 'day' | 'evening' | 'night';

export const TIME_PRESETS: Record<TimePreset, TimeOfDay> = {
  day: 0,
  evening: 0.55,
  night: 1,
};

export function timePresetFor(t: TimeOfDay): TimePreset {
  if (t < 0.28) return 'day';
  if (t < 0.8) return 'evening';
  return 'night';
}
